import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export type Density = "comfortable" | "compact";

const STORAGE_KEY = "doctorsx-density";

type DensityCtx = {
  density: Density;
  setDensity: (d: Density) => void;
  toggle: () => void;
};

const Ctx = createContext<DensityCtx | undefined>(undefined);

function readStored(): Density {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === "compact" ? "compact" : "comfortable";
  } catch {
    return "comfortable";
  }
}

export function DensityProvider({ children }: { children: ReactNode }) {
  const [density, setDensityState] = useState<Density>(() =>
    typeof window === "undefined" ? "comfortable" : readStored(),
  );

  useEffect(() => {
    document.documentElement.setAttribute("data-density", density);
    try {
      window.localStorage.setItem(STORAGE_KEY, density);
    } catch {}
  }, [density]);

  const setDensity = (d: Density) => setDensityState(d);
  const toggle = () => setDensityState((d) => (d === "compact" ? "comfortable" : "compact"));

  return <Ctx.Provider value={{ density, setDensity, toggle }}>{children}</Ctx.Provider>;
}

export function useDensity() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useDensity must be used within DensityProvider");
  return ctx;
}
